import { Actions, DockLocation, Layout as FlexLayout } from "flexlayout-react";
import { findPanelTypeIds } from "./layout-utils";

import { type ReactNode, useCallback, useMemo, useRef } from "react";
import { useLayouts } from "./layout-list-context";
import { useModel } from "./layout-model";
import { ActiveLayoutContext, type Config } from "./layout-active-context";

type ActiveLayoutProviderProps = {
  children: ReactNode;
  config: Config;
};

export const ActiveLayoutProvider = ({
  children,
  config,
}: ActiveLayoutProviderProps) => {
  const { selectedLayout, updateLayoutTree } = useLayouts();
  const { id, tree } = selectedLayout.data;
  const layoutRef = useRef<FlexLayout | null>(null);
  const { model, onModelChange } = useModel({
    id,
    tree,
    config,
    updateLayoutTree,
  });

  const existingPanelIds = useMemo(() => findPanelTypeIds(tree), [tree]);

  const addPanel = useCallback(
    (id: string, nodeId: string) => {
      const panelConfig = config[id];
      if (!panelConfig) {
        console.error(`Panel "${id}" is invalid`);
        return;
      }

      model.doAction(
        Actions.addNode(
          {
            component: id,
            name: panelConfig.name,
          },
          nodeId,
          DockLocation.CENTER,
          -1,
          true
        )
      );
    },
    [model, config]
  );

  const onDragAdd = useCallback(
    (ev: DragEvent, configId: string) => {
      if (!layoutRef.current) {
        console.error("Layout ref is not set");
        return;
      }

      const panelConfig = config[configId];
      if (!panelConfig) {
        console.error(`Panel "${configId}" is invalid`);
        return;
      }

      const nodeConfig = {
        component: configId,
        name: panelConfig.name,
      };
      layoutRef.current.addTabWithDragAndDrop(ev, nodeConfig, () => {});
    },
    [config]
  );

  const options = useMemo(() => {
    const opts = Object.keys(config).map((key) => {
      const panelConfig = config[key];

      return {
        id: key,
        name: panelConfig.name,
        isDisabled: existingPanelIds.has(key),
      };
    });

    return opts.sort((a, b) => a.name.localeCompare(b.name));
  }, [config, existingPanelIds]);

  return (
    <ActiveLayoutContext.Provider
      value={{
        model: model,
        addPanel,
        config,
        options,
        onDragAdd,
        layoutRef,
        onModelChange,
      }}
    >
      {children}
    </ActiveLayoutContext.Provider>
  );
};
